import React, { useEffect, useMemo, useState } from "react";
import { FaExternalLinkAlt, FaPlayCircle } from "react-icons/fa";
import { getElakayLanguageCode } from "./dataElakay";
import {
  configuredProfessionalVideos,
  selectProfessionalVideoLanguage
} from "./professionalVideo";
import "./ProfessionalVideoPlayer.css";

const platformLabelKey = {
  youtube: "watchOnYouTube",
  facebook: "watchOnFacebook",
  tiktok: "watchOnTikTok"
};

const platformFallback = {
  youtube: "Watch on YouTube",
  facebook: "Watch on Facebook",
  tiktok: "Watch on TikTok"
};

const ProfessionalVideoPlayer = ({ professionalVideos, tr, title = true, activeLanguage }) => {
  const videos = useMemo(() => configuredProfessionalVideos(professionalVideos), [professionalVideos]);
  const interfaceLanguage = activeLanguage || getElakayLanguageCode();
  const automaticLanguage = selectProfessionalVideoLanguage(professionalVideos, interfaceLanguage);
  const [selectedLanguage, setSelectedLanguage] = useState(automaticLanguage);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => setSelectedLanguage(automaticLanguage), [automaticLanguage]);

  const selected = videos.find((video) => video.language === selectedLanguage) || videos[0];
  useEffect(() => {
    setFailed(false);
    setLoading(Boolean(selected?.embedUrl));
  }, [selected?.embedUrl]);

  if (!selected) return null;

  const openLabel = selected.platform in platformLabelKey
    ? tr(platformLabelKey[selected.platform], platformFallback[selected.platform])
    : tr("watchOriginalVideo", "Watch Original Video");
  const showEmbed = Boolean(selected.embedUrl) && !failed;

  return (
    <section className="professional-video" aria-label={tr("professionalIntroductionVideo", "Professional Introduction Video")}>
      {title && (
        <div className="professional-video__heading">
          <FaPlayCircle aria-hidden="true" />
          <div>
            <h4>{tr("professionalIntroductionVideo", "Professional Introduction Video")}</h4>
            <span>{selected.platform === "external" ? tr("externalVideo", "External video") : selected.platform}</span>
          </div>
        </div>
      )}

      <div className={`professional-video__stage${selected.vertical ? " is-vertical" : ""}`}>
        {showEmbed ? (
          <>
            {loading && <div className="professional-video__loading">{tr("loadingVideo", "Loading video…")}</div>}
            <iframe
              key={selected.embedUrl}
              src={selected.embedUrl}
              title={`${tr("professionalIntroductionVideo", "Professional Introduction Video")} — ${selected.language.toUpperCase()}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              onLoad={() => setLoading(false)}
              onError={() => { setLoading(false); setFailed(true); }}
            />
          </>
        ) : (
          <div className="professional-video__fallback">
            <FaPlayCircle aria-hidden="true" />
            <strong>{failed ? tr("videoUnavailable", "Video Unavailable") : tr("externalVideo", "External video")}</strong>
            <p>{tr("openVideoHelp", "Open this presentation securely on its original platform.")}</p>
          </div>
        )}
      </div>

      <div className="professional-video__controls">
        {videos.length > 1 && (
          <div className="professional-video__languages" aria-label={tr("chooseVideoLanguage", "Choose Video Language")}>
            {videos.map((video) => (
              <button
                type="button"
                key={video.language}
                className={video.language === selected.language ? "active" : ""}
                aria-pressed={video.language === selected.language}
                onClick={() => setSelectedLanguage(video.language)}
              >
                {video.language.toUpperCase()}
              </button>
            ))}
          </div>
        )}
        <a href={selected.originalUrl} target="_blank" rel="noopener noreferrer">
          {openLabel} <FaExternalLinkAlt aria-hidden="true" />
        </a>
      </div>
    </section>
  );
};

export default ProfessionalVideoPlayer;
